import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { COLORS, DESIGN_W, BORDER } from '../app/theme';
import { viewport } from '../app/viewport';
import { Panel, Button, makeText } from './widgets';
import { scaleIn, fadeTo, animate } from '../app/anim';
import { CHAT_PHRASES } from '../net/codec/chat';

export interface ChatAnchor {
  x: number;
  y: number;
}

export interface ChatControlOpts {
  /** Resolve a respack atlas frame (for the speech-bubble background), or undefined. */
  respackFrame: (name: string) => Texture | undefined;
  onSend: (text: string) => void;
  /** Bubble anchor points (centre of where the bubble should hover) for each side. */
  mySide: ChatAnchor;
  oppSide: ChatAnchor;
}

/**
 * The in-game quick-chat: a گپ flyout listing the predefined phrases (tap → send), plus transient
 * speech bubbles over each player's avatar. Mirrors Unity's TableInformation chat list + My/Opp
 * chat panels (bubbles auto-hide after ~2 s). One instance is held by the table scene's overlay.
 */
export class ChatControl extends Container {
  private flyout: Container | null = null;
  private readonly bubbles = new Container();
  private myBubble: Container | null = null;
  private oppBubble: Container | null = null;

  constructor(private readonly opts: ChatControlOpts) {
    super();
    this.addChild(this.bubbles);
  }

  /** Open/close the phrase picker. */
  toggle(): void {
    if (this.flyout) this.closeFlyout();
    else this.openFlyout();
  }

  private openFlyout(): void {
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: 0.55 });
    dim.eventMode = 'static';
    dim.on('pointertap', () => this.closeFlyout());
    layer.addChild(dim);

    const boxW = 560;
    const rowH = 58;
    const gap = 10;
    const top = 96;
    const boxH = top + CHAT_PHRASES.length * (rowH + gap) + 86;
    const panel = new Panel(boxW, boxH);
    panel.position.set((DESIGN_W - boxW) / 2, (viewport.h - boxH) / 2);
    layer.addChild(panel);

    const title = makeText('گفتگو', { size: 30, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 24);
    panel.addChild(title);

    CHAT_PHRASES.forEach((phrase, i) => {
      const btn = new Button(
        phrase,
        () => {
          this.opts.onSend(phrase);
          this.closeFlyout();
        },
        { width: boxW - 64, height: rowH, fontSize: 22, fill: COLORS.panelSoft, textColor: COLORS.text },
      );
      btn.position.set(32, top + i * (rowH + gap));
      panel.addChild(btn);
    });

    const close = new Button('بستن', () => this.closeFlyout(), {
      width: boxW - 64,
      height: 56,
      fontSize: 22,
    });
    close.position.set(32, boxH - 70);
    panel.addChild(close);

    this.flyout = layer;
    this.addChild(layer);
    scaleIn(panel, 220, 0.85);
  }

  private closeFlyout(): void {
    if (!this.flyout) return;
    const f = this.flyout;
    this.flyout = null;
    if (!f.destroyed) f.destroy({ children: true });
  }

  /** Pop a transient speech bubble over the given side's avatar (auto-hides after ~2 s). */
  showBubble(side: 'me' | 'opp', text: string): void {
    const anchor = side === 'me' ? this.opts.mySide : this.opts.oppSide;
    const prev = side === 'me' ? this.myBubble : this.oppBubble;
    if (prev && !prev.destroyed) prev.destroy({ children: true });

    const bubble = this.buildBubble(text);
    bubble.position.set(anchor.x, anchor.y);
    this.bubbles.addChild(bubble);
    if (side === 'me') this.myBubble = bubble;
    else this.oppBubble = bubble;

    scaleIn(bubble, 240, 0.6);
    // Hold, then fade out and destroy.
    animate({
      duration: 2400,
      bind: bubble,
      onUpdate: () => {},
      onComplete: () => {
        if (bubble.destroyed) return;
        fadeTo(bubble, 0, 260);
        animate({
          duration: 300,
          bind: bubble,
          onUpdate: () => {},
          onComplete: () => {
            if (!bubble.destroyed) bubble.destroy({ children: true });
          },
        });
      },
    });
  }

  /** A speech bubble = the chatbox-left frame (if available) behind centered text, else a Panel. */
  private buildBubble(text: string): Container {
    const c = new Container();
    const label = makeText(text, { size: 22, weight: '700', align: 'center', fill: COLORS.white });
    label.anchor.set(0.5);

    const padX = 28;
    const padY = 18;
    const w = Math.max(140, label.width + padX * 2);
    const h = label.height + padY * 2;

    const frame = this.opts.respackFrame('chatbox-left');
    if (frame) {
      const s = new Sprite(frame);
      s.width = w;
      s.height = h;
      s.anchor.set(0.5);
      c.addChild(s);
    } else {
      const g = new Graphics().roundRect(-w / 2, -h / 2, w, h, 16).fill({ color: COLORS.darkPill, alpha: 0.95 }).stroke({ width: BORDER.base, color: COLORS.gold });
      c.addChild(g);
    }
    c.addChild(label);
    return c;
  }
}
